import type { BrandInsightsPageContext, Chat } from '@trinity/chat';
import { ChatContextType, resolveSubjectScopedKind } from '@trinity/chat';
import { generateEmptyChatSessionFiltersState } from '@trinity/chat-components/components/Chat.State/state.initial-state';

export function calculateIfFiltersActive(data: CalculateActiveCount) {
  return calculateActiveFiltersCount(data) > 0;
}

export function calculateActiveFiltersCount({ context, data, features }: CalculateActiveCount) {

  let count = data.custom.length;
  const defaults = generateEmptyChatSessionFiltersState({ context, features });

  count += countStandardFilterDiffs(data, defaults, context);
  count += countEngagementFilter(data, features, context);
  count += countContextScopedFilter(context);

  return count;
}

/**
 * Project/Document/Folder/Brand/Transcript contexts scope the chat to a single entity
 * (via `context.data.subject` for Agent contexts) rather than `data.projects`/`data.records`.
 */
export function isSubjectScopedContext(context: BrandInsightsPageContext.Item): boolean {
  return resolveSubjectScopedKind(context) !== null;
}

/**
 * Counts a subject-scoped context's selection as an active filter so the Source Filter
 * badge reflects it — see `isSubjectScopedContext`.
 */
function countContextScopedFilter(context: BrandInsightsPageContext.Item): number {
  if (isSubjectScopedContext(context)) {
    if (context.type === ChatContextType.Agent) {
      return context.data.subject != null ? 1 : 0;
    }
    // A direct (non-Agent) Project context uses the plain `filters.projects`
    // multi-select (see Project.tsx's `resolveMode`), which `countStandardFilterDiffs`
    // already counts — counting it again here would double-count the same selection.
    if (context.type === ChatContextType.Project) {
      return 0;
    }
    // Direct (non-Agent) Document/Folder/Brand/Transcript contexts always
    // carry a required entity id — there's no "unselected" state to guard,
    // so the context itself always represents an active scope.
    return 1;
  }

  return 0;
}

function sortedById(items: { id: string }[] | undefined | null) {
  return JSON.stringify((items || []).concat().sort((a, b) => a.id.localeCompare(b.id)));
}

function countStandardFilterDiffs(data: Chat.Filters, defaults: Chat.Filters, context: BrandInsightsPageContext.Item): number {
  let count = 0;
  if (sortedById(data.sources) != sortedById(defaults.sources)) count++;
  if (data.minDocumentDate != defaults.minDocumentDate) count++;
  if (data.tags.length) count++;
  if (sortedById(data.dataSources) != sortedById(defaults.dataSources)) count++;
  if (data.records?.length && [
    ChatContextType.Global,
    ChatContextType.KolProfile,
  ].includes(context.type)) {
    count += data.records.length;
  }
  if (data.projects.length) count++;
  return count;
}

function countEngagementFilter(data: Chat.Filters, features: CalculateActiveCount['features'], context: BrandInsightsPageContext.Item): number {
  if (features.engagementFilter && data.engagement?.id !== 'all' && [
    ChatContextType.Global,
    ChatContextType.KolProfile,
  ].includes(context.type)) {
    return 1;
  }
  return 0;
}

type CalculateActiveCount = {
  context: BrandInsightsPageContext.Item;
  data: Chat.Filters;
  features: {
    hcpProfiles: boolean;
    workspaces: boolean;
    engagementFilter: boolean;
  }
};
